import { BadgeVariant } from '@Components';
import { FirmStoreSummary, Schedule, ScheduleTask, ScheduleTaskReviewStatus, ScheduleWeek, TaskType } from '@Models';
import { addDaysToDateString } from '../Wizard/constants';

type ReviewStatusFilter = 'pending' | 'approved' | 'rejected';

const REVIEW_STATUS_TO_FILTER: Record<ScheduleTaskReviewStatus, ReviewStatusFilter | null> = {
  draft: 'pending',
  pending_review: 'pending',
  approved: 'approved',
  rejected: 'rejected',
  published: null,
};

const REVIEW_STATUS_FILTER_TO_WRITE: Record<ReviewStatusFilter, ScheduleTaskReviewStatus> = {
  pending: 'pending_review',
  approved: 'approved',
  rejected: 'rejected',
};

const REVIEW_STATUS_FILTER_VARIANT: Record<ReviewStatusFilter, BadgeVariant> = {
  pending: 'gray',
  approved: 'green',
  rejected: 'red',
};

const REVIEW_STATUS_FILTER_VALUES: ReviewStatusFilter[] = ['pending', 'approved', 'rejected'];

const getReviewStatusFilter = (reviewStatus: ScheduleTaskReviewStatus): ReviewStatusFilter | null =>
  REVIEW_STATUS_TO_FILTER[reviewStatus];

interface RankedTask {
  task: ScheduleTask;
  effectiveRank: number;
}

const sortTasksByPriority = (tasks: ScheduleTask[]): RankedTask[] => {
  const byCreatedAt = [...tasks].sort((a, b) => a.created_at.localeCompare(b.created_at));
  let unrankedCount = 0;
  const ranked = byCreatedAt.map((task) => {
    const effectiveRank = task.priority_rank ?? Number.MAX_SAFE_INTEGER - 1_000_000 + unrankedCount;
    unrankedCount += task.priority_rank == null ? 1 : 0;
    return { task, effectiveRank };
  });
  return ranked.sort((a, b) => a.effectiveRank - b.effectiveRank);
};

const getDistinctAreas = (tasks: ScheduleTask[]): string[] =>
  Array.from(new Set(tasks.map((task) => task.area).filter((area): area is string => Boolean(area)))).sort();

const reorderTasksByDrag = (allTasks: ScheduleTask[], fromTaskId: string, toTaskId: string): ScheduleTask[] | null => {
  if (fromTaskId === toTaskId) return null;

  const sorted = sortTasksByPriority(allTasks).map((entry) => entry.task);
  const fromIndex = sorted.findIndex((task) => task.id === fromTaskId);
  const toIndex = sorted.findIndex((task) => task.id === toTaskId);
  if (fromIndex === -1 || toIndex === -1) return null;

  const reordered = [...sorted];
  const [moved] = reordered.splice(fromIndex, 1);
  reordered.splice(toIndex, 0, moved);
  return reordered;
};

const countPendingTasks = (tasks: ScheduleTask[], notContractedTaskIds: Set<string> = new Set()): number =>
  tasks.filter((task) => getReviewStatusFilter(task.review_status) === 'pending' && !notContractedTaskIds.has(task.id))
    .length;

const getWeekRowCount = (task: ScheduleTask): number =>
  Math.max(1, task.categories.length + (task.has_uncategorized_products ? 1 : 0));

const getNotContractedTaskIds = (tasks: ScheduleTask[], plannableTaskTypes: TaskType[]): Set<string> => {
  const plannableTaskTypeIds = new Set(plannableTaskTypes.map((taskType) => taskType.id));
  return new Set(tasks.filter((task) => !plannableTaskTypeIds.has(task.task_type_id)).map((task) => task.id));
};

const isListLocked = (tasks: ScheduleTask[]): boolean => tasks.some((task) => task.review_status === 'published');

const getTasksThatMayNotFit = (
  rankedTasks: RankedTask[],
  roomSlots: number,
  isInheritedList = false,
  notContractedTaskIds: Set<string> = new Set(),
): Set<string> => {
  const mayNotFit = new Set<string>();
  let usedSlots = 0;
  rankedTasks
    .filter((entry) => entry.task.review_status !== 'rejected' && !notContractedTaskIds.has(entry.task.id))
    .forEach((entry) => {
      usedSlots += isInheritedList ? getWeekRowCount(entry.task) : 1;
      if (usedSlots > roomSlots) mayNotFit.add(entry.task.id);
    });
  return mayNotFit;
};

/** Tasks past the week's free slots, ranked over the whole list; empty while the room is unknown. */
const getMayNotFitTaskIds = (
  allTasks: ScheduleTask[],
  roomSlots: number | null,
  isInheritedList: boolean,
  notContractedTaskIds: Set<string>,
): Set<string> =>
  roomSlots === null
    ? new Set<string>()
    : getTasksThatMayNotFit(sortTasksByPriority(allTasks), roomSlots, isInheritedList, notContractedTaskIds);

type ScheduleComposition = 'scheduled_only' | 'alerts_only' | 'hybrid';

const getScheduleComposition = (alertCeilingPct: number): ScheduleComposition => {
  if (alertCeilingPct === 0) return 'scheduled_only';
  if (alertCeilingPct === 100) return 'alerts_only';
  return 'hybrid';
};

const getAlertCeilingDisplay = (alertCeilingPct: number): number | null =>
  getScheduleComposition(alertCeilingPct) === 'scheduled_only' ? null : alertCeilingPct;

const MOCK_TYPICAL_VISIT_SIZE = 16;

const getTotalVisitSlots = (firms: FirmStoreSummary[]): number => {
  const maxValues = firms
    .map((firm) => firm.visit_task_target_max)
    .filter((value): value is number => value != null && value > 0);
  return maxValues.length > 0 ? Math.max(...maxValues) : MOCK_TYPICAL_VISIT_SIZE;
};

interface AlertCeilingUtilizationMetrics {
  capSlots: number;
  roomSlots: number;
  utilizationPct: number;
}

const getAlertCeilingUtilization = (capPct: number, totalSlots: number): AlertCeilingUtilizationMetrics => {
  if (totalSlots <= 0) return { capSlots: 0, roomSlots: 0, utilizationPct: 0 };
  const capSlots = Math.round((capPct / 100) * totalSlots);
  const roomSlots = Math.max(0, totalSlots - capSlots);
  const utilizationPct = Math.round((capSlots / totalSlots) * 100);
  return { capSlots, roomSlots, utilizationPct };
};

const WEEKS_RANGE_LOOKAHEAD_DAYS = 182;
const MAX_VISIBLE_WEEKS = 4;

const toIsoDate = (date: Date): string => {
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${date.getFullYear()}-${month}-${day}`;
};

interface ScheduleWeeksRange {
  from: string;
  to: string;
}

const getScheduleWeeksRange = (today: Date): ScheduleWeeksRange => {
  const from = toIsoDate(today);
  return { from, to: addDaysToDateString(from, WEEKS_RANGE_LOOKAHEAD_DAYS) };
};

const getVisibleWeeks = (weeks: ScheduleWeek[]): ScheduleWeek[] => weeks.slice(0, MAX_VISIBLE_WEEKS);

const WEEKDAY_TOKENS = ['SU', 'MO', 'TU', 'WE', 'TH', 'FR', 'SA'];
const CADENCE_CALENDAR_WEEKS = 4;

const dateStringWeekday = (date: string): number => new Date(`${date}T00:00:00Z`).getUTCDay();

const getCadenceWeekdays = (schedule: Pick<Schedule, 'cadence_days'>): number[] => {
  const indexes = (schedule.cadence_days ?? [])
    .map((day) =>
      typeof day === 'string'
        ? WEEKDAY_TOKENS.indexOf(day.slice(-2).toUpperCase())
        : Number.isInteger(day) && day >= 0 && day <= 6
        ? (day + 1) % 7
        : -1,
    )
    .filter((index) => index >= 0);
  return Array.from(new Set(indexes)).sort((a, b) => a - b);
};

const getCadenceWindowStart = (weeks: ScheduleWeek[], today: string): string => {
  const first = weeks[0];
  if (first === undefined) return today;
  const offset = (dateStringWeekday(today) - dateStringWeekday(first.week_start) + 7) % 7;
  return addDaysToDateString(today, -offset);
};

const getScheduleVisitDates = (
  schedule: Pick<Schedule, 'cadence' | 'cadence_days' | 'start_date' | 'end_date'>,
  weeks: ScheduleWeek[],
  windowStart: string,
  windowEnd: string,
): string[] => {
  const within = (date: string): boolean =>
    date >= windowStart &&
    date <= windowEnd &&
    date >= schedule.start_date &&
    (schedule.end_date === null || date <= schedule.end_date);

  if (schedule.cadence === 'once') return within(schedule.start_date) ? [schedule.start_date] : [];

  const weekdays = getCadenceWeekdays(schedule);
  const dates = weeks.flatMap((week) => {
    const base = dateStringWeekday(week.week_start);
    return weekdays.map((weekday) => addDaysToDateString(week.week_start, (weekday - base + 7) % 7));
  });
  return Array.from(new Set(dates.filter(within))).sort();
};

const buildCadenceCalendar = (windowStart: string, visitDates: string[], today: string): CadenceCell[] => {
  const visits = new Set(visitDates);
  return Array.from({ length: CADENCE_CALENDAR_WEEKS * 7 }, (_unused, offset) => {
    const date = addDaysToDateString(windowStart, offset);
    return {
      date,
      dayOfMonth: Number(date.slice(8, 10)),
      isVisit: visits.has(date),
      isToday: date === today,
    };
  });
};

const canEditScheduleWeek = (week: ScheduleWeek): boolean => !week.has_started;

interface CadenceCell {
  date: string;
  dayOfMonth: number;
  isVisit: boolean;
  isToday: boolean;
}

type ListReadOnlyReason = 'week_started' | 'schedule_ended';

const getListReadOnlyReason = (
  week: ScheduleWeek | undefined,
  schedule: Pick<Schedule, 'start_date' | 'end_date'>,
  today: Date,
  hasNoVisitWeeksLeft: boolean,
): ListReadOnlyReason | null => {
  if (week) return week.has_started ? 'week_started' : null;
  const todayIso = toIsoDate(today);
  const hasEnded = schedule.end_date !== null && schedule.end_date < todayIso;
  const hasRunOutOfVisits = hasNoVisitWeeksLeft && schedule.start_date <= todayIso;
  return hasEnded || hasRunOutOfVisits ? 'schedule_ended' : null;
};

const formatWeekOf = (weekStart: string, language: string): string =>
  new Intl.DateTimeFormat(language, { month: 'short', day: 'numeric', timeZone: 'UTC' }).format(
    new Date(`${weekStart}T00:00:00Z`),
  );

export {
  buildCadenceCalendar,
  CADENCE_CALENDAR_WEEKS,
  canEditScheduleWeek,
  countPendingTasks,
  formatWeekOf,
  getAlertCeilingDisplay,
  getAlertCeilingUtilization,
  getCadenceWeekdays,
  getCadenceWindowStart,
  getDistinctAreas,
  getListReadOnlyReason,
  getMayNotFitTaskIds,
  getNotContractedTaskIds,
  getReviewStatusFilter,
  getScheduleComposition,
  getScheduleVisitDates,
  getScheduleWeeksRange,
  getTasksThatMayNotFit,
  getTotalVisitSlots,
  getVisibleWeeks,
  isListLocked,
  reorderTasksByDrag,
  REVIEW_STATUS_FILTER_TO_WRITE,
  REVIEW_STATUS_FILTER_VALUES,
  REVIEW_STATUS_FILTER_VARIANT,
  sortTasksByPriority,
};
export type { CadenceCell, ListReadOnlyReason, RankedTask, ReviewStatusFilter };
